import React, { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { deepClone } from 'helper/helper';
import PropTypes from 'prop-types';
import {
	setSelectedList,
	setFilterList,
	addPreviewColumn,
	removePreviewColumn
} from '../Preview.actions';
import AttrList from './AttrList.component';
import { PreviewListItemContext } from '../Preview.context';
import { isCalFamily, isMultiWeekSel } from '../filter/FilterCondition';
import { UserPortalContext } from 'context/appcontext';
import { FEATURES } from 'helper/basicFeatureFlag';
import {
	BUSINESS_DATE,
	WALMART_CALENDAR,
	MULTI_TIME_SELECTION,
	PIVOT_SINGLE_TIME_VALUES,
	PIVOT_SINGLE_TIME_VALUE_MAP,
	PIVOT_MTS_VALUE
} from 'constants/constant';
import { isMTS } from 'components/Preview/filter/FilterCondition';
import { hasPivotedColumn } from '../Preview.utils';

function StoreTimeElements({ list, accordianName, templateId, showCheckbox }) {
	const [checked, setChecked] = useState([]);
	const dispatch = useDispatch();
	const portalUserContext = useContext(UserPortalContext);
	const selectedList = useSelector((state) => state.previewReducer.selectedList);
	const usrFilterList = useSelector((state) => state.previewReducer.filterList);
	const { previewData, pivotOpts } = useSelector((state) => state.previewReducer);
	const editData = useSelector((state) => state.dashboardReducer?.editData);
	const isEditMode = Object.keys(editData ?? {}).length > 0;
	const { customerType } = portalUserContext;
	const { DISABLE_GREGORIAN_CALENDAR: disableGregorianCalendar } =
		FEATURES[customerType].PREVIEW_PAGE;
	const pivotColumn = pivotOpts?.pivotColumn;

	useEffect(() => {
		setChecked(selectedList);
	}, []);

	const handleListClick = (event, value) => {
		event.stopPropagation();
		if (hasPivotedColumn(pivotColumn) && PIVOT_SINGLE_TIME_VALUES.includes(value?.technicalName)) {
			/**
			 * If Pivot is applied on any one of the time attributes,
			 * Then this time attribute should not be able to deselect & no operations are allowed further
			 */
			return;
		}
		/**
		 * If multi time selection has been done then user cannot select/de-select the field to be shown on the table
		 * Hence, return without doing anything
		 */
		if (value.businessName === 'Week') {
			const index = usrFilterList.findIndex(
				(el) => el.businessName === value.businessName && isMultiWeekSel(el, Number(templateId))
			);
			if (index > -1) {
				return;
			}
		}
		const index = checked.findIndex((el) => {
			return el.businessName === value.businessName && el.parent === accordianName;
		});

		const selectedList = deepClone(checked);
		const lFilterList = deepClone(usrFilterList);
		if (index === -1) {
			selectedList.unshift({ ...value, parent: accordianName });
			dispatch(addPreviewColumn(previewData, value.technicalName));
		} else {
			selectedList.splice(index, 1);
			dispatch(removePreviewColumn(previewData, value.technicalName));
		}
		setChecked(selectedList);
		dispatch(setSelectedList(selectedList));
		dispatch(setFilterList(lFilterList));
	};

	const checkCalMember = () => {
		const lCheck = checked.some((el) => isCalFamily(el));
		const lFilter = usrFilterList.some((el) => isCalFamily(el) && el.field !== undefined);
		return lCheck || lFilter;
	};

	const shouldCheck = (value) => {
		const mtsFilter = usrFilterList.find(
			(el) => el.businessName === value.businessName && isMultiWeekSel(el, Number(templateId))
		);
		/** If MTS 2.0 is applied. then it should always return false then day, week, month, quarter and year will all be greyed out*/
		if (isMTS(mtsFilter)) {
			return false;
		}
		if (hasPivotedColumn(pivotColumn)) {
			// Since we are changing the pivotColumn value to wm_time_window_week, when we pull the data from BE
			// in Edit mode, the technicalName doesnt match, so hardcoding is required to check whether it is happening.
			// Reference: handleScheduleCreate function in ReportSettings.component.js
			const wmtCalWeeks = [
				PIVOT_SINGLE_TIME_VALUE_MAP.WMT_CAL_WEEK,
				PIVOT_SINGLE_TIME_VALUE_MAP.WMT_YEAR_AND_WEEK
			];
			// If current column is week and pivot is applied on Walmart Calendar Week
			// then it should be checked
			const isMultiTimePivotApplied =
				wmtCalWeeks.includes(value?.technicalName) && pivotColumn === PIVOT_MTS_VALUE;

			let isCurrentColumnThePivotColumn;
			if (isEditMode) {
				isCurrentColumnThePivotColumn =
					value?.technicalName === pivotColumn || isMultiTimePivotApplied;
			} else {
				isCurrentColumnThePivotColumn =
					value?.technicalName === pivotColumn && !isMultiTimePivotApplied;
			}
			/**
			 * While Pivot has applied on a Walmart Cal. time attribute,
			 * it should check automatically, other items should be unchecked.
			 */
			return isCurrentColumnThePivotColumn && accordianName === WALMART_CALENDAR;
		}
		return checked
			?.filter((filter) => filter?.localName !== MULTI_TIME_SELECTION)
			.some((el) => el.businessName === value.businessName && el.parent === accordianName);
	};

	const checkFilter = (value) => {
		return usrFilterList.some((el) => {
			// if is it MTS, then disable the rest of the sub attributes
			if (el?.localName === MULTI_TIME_SELECTION) {
				return false;
			}
			return (
				el.businessName === value.businessName &&
				el.parent === accordianName &&
				el.field !== undefined
			);
		});
	};

	const shouldDisable = (value) => {
		// For Specific customer type(Ex: Basic users) 'Gregorian Calendar' should be disabled
		if (disableGregorianCalendar && accordianName === 'Gregorian Calendar') {
			return true;
		}
		if (checked.length === 0 && usrFilterList.length === 0) {
			return false;
		}
		if (!checkCalMember()) {
			return false;
		}
		if (shouldCheck(value)) {
			return false;
		}
		return !checkFilter(value);
	};

	const singleTimeAttributes = [...PIVOT_SINGLE_TIME_VALUES, BUSINESS_DATE];

	const shouldCheckboxDisable = (e) => {
		/**
		 * If Pivot is applied on any one of the time attributes,
		 * Then all other Single Time attribute checkbox should be disabled
		 */
		const timeElement = e?.technicalName;
		const hasPivoted =
			hasPivotedColumn(pivotColumn) &&
			pivotColumn !== e?.technicalName &&
			singleTimeAttributes.includes(timeElement);
		return hasPivoted;
	};

	return (
		<PreviewListItemContext.Provider
			value={{
				cbHandleListClick: handleListClick
			}}>
			{list?.map((el) => {
				const itemChecked = shouldCheck(el);
				const itemDisable = shouldDisable(el);
				const checkboxDisabled = shouldCheckboxDisable(el);
				return (
					<AttrList
						item={el}
						disabled={itemDisable}
						key={el?.technicalName}
						checked={itemChecked}
						paddingLeft={showCheckbox ? 0 : 26}
						parent={accordianName}
						showCheckbox={showCheckbox}
						checkboxDisabled={checkboxDisabled}
					/>
				);
			})}
		</PreviewListItemContext.Provider>
	);
}

StoreTimeElements.propTypes = {
	list: PropTypes.array.isRequired,
	accordianName: PropTypes.string.isRequired,
	templateId: PropTypes.string.isRequired
};

export default StoreTimeElements;
